/**
 * PLAN MANAGER SIMPLIFICADO - Sin checkboxes, UI directa
 */

class PlanManagerV2 {
    constructor() {
        this.modal = null;
        this.overlay = null;
        this.isOpen = false;
        this.currentAccount = null;
        this.existingPlan = null;
        this.planValues = {};
        this.planCache = {}; // FIX: Cache indexado por accountId para evitar compartir entre cuentas
        this.planCacheTime = {}; // FIX: Tiempo de cache también por accountId
        this.dailyChecklists = {}; // FIX: Checklists por cuenta, no global
        this.init();
    }

    init() {
        this.createModal();
        this.setupEventListeners();
    }

    getPlanStorage() {
        if (typeof planStorage !== 'undefined') return planStorage;
        if (typeof window !== 'undefined' && window.planStorage) return window.planStorage;
        return null;
    }

    createModal() {
        // Overlay que contiene el modal
        this.overlay = document.createElement('div');
        this.overlay.id = 'planManagerOverlay';
        this.overlay.className = 'fixed inset-0 bg-black/80 z-[9990] flex items-center justify-center p-4 hidden';

        // Modal dentro del overlay (MISMO patrón que dayModal)
        this.modal = document.createElement('div');
        this.modal.id = 'planManagerModal';
        this.modal.className = 'bg-slate-900 rounded-xl border border-slate-700 w-full max-w-2xl max-h-[60vh] sm:max-h-[80vh] shadow-2xl flex flex-col overflow-hidden min-h-0';
        this.modal.addEventListener('click', (e) => e.stopPropagation());

        // Meter modal dentro del overlay
        this.overlay.appendChild(this.modal);

        // Cerrar al hacer click en el overlay (fuera del modal)
        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay) {
                this.close();
            }
        });

        document.body.appendChild(this.overlay);
    }

    setupEventListeners() {
        const btn = document.getElementById('followPlanBtn');
        if (btn) {
            btn.addEventListener('click', () => this.open());
        }

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.close();
            }
        });
        
        // Update button label once after a short delay
        setTimeout(() => this.updateButtonLabel(), 200);

        if (typeof window !== 'undefined') {
            // FIX: Hacer que window.updateFollowPlanButton sea async para esperar a que se complete
            // y acepte accountId como parámetro
            window.updateFollowPlanButton = (accountId) => this.updateButtonLabel(accountId);
        }
    }

    async updateButtonLabel(accountId = null, skipCache = false) {
        const btn = document.getElementById('followPlanBtn');
        if (!btn) return;

        // FIX: Usar accountId pasado como parametro, o si no, usar window.currentAccount
        const finalAccountId = accountId || window.currentAccount;
        if (!finalAccountId) {
            btn.style.display = 'none';
            return;
        }

        if (!window.accounts || !window.accounts[finalAccountId]) {
            setTimeout(() => this.updateButtonLabel(accountId, skipCache), 250);
            return;
        }

        if (!this.isCurrentAccountSynced(finalAccountId)) {
            btn.style.display = 'none';
            return;
        }
        btn.style.display = 'flex';
        
        // FIX: Si skipCache=true, leer localStorage INMEDIATAMENTE sin esperar API
        // Actualizar desde API en background sin bloquear UI
        if (skipCache) {
            const localState = this.getPlanState(finalAccountId);
            const isLocked = this.isCreationLocked(finalAccountId);
            
            // Mostrar estado local instantáneamente
            if (localState === 'active') {
                btn.innerHTML = `
                    <div class="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    Plan Activo
                `;
                btn.className = 'relative px-4 py-2 bg-green-600/20 text-green-400 font-medium rounded-lg border border-green-500/50 hover:bg-green-600/30 transition-all duration-300 flex items-center gap-2 text-sm';
            } else if (isLocked) {
                const when = this.formatNextAllowedAt(this.getNextAllowedAt(finalAccountId));
                btn.innerHTML = `
                    <div class="w-2 h-2 bg-amber-400 rounded-full"></div>
                    Plan en espera
                `;
                btn.title = when ? `Disponible: ${when}` : 'Disponible al iniciar el próximo día';
                btn.className = 'relative px-4 py-2 bg-amber-600/10 text-amber-300 font-medium rounded-lg border border-amber-500/40 cursor-not-allowed transition-all duration-300 flex items-center gap-2 text-sm';
            } else {
                // Mostrar "Crear Plan" si no hay estado local y no está locked
                this.clearPlanState(finalAccountId);
                btn.innerHTML = `
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                    </svg>
                    Crear Plan
                `;
                btn.className = 'px-4 py-2 bg-slate-800 text-slate-300 font-medium rounded-lg border border-slate-600 hover:bg-slate-700 hover:border-cyan-500/50 transition-all duration-300 flex items-center gap-2 text-sm';
            }
            
            // Actualizar desde API en background (sin esperar)
            this.getPlanCached(true, finalAccountId).then(plan => {
                // Si el plan se actualizó, refrescar el botón
                if (plan) {
                    const newState = this.getPlanState(finalAccountId) || 'created';
                    if (newState !== localState) {
                        this.updateButtonLabel(finalAccountId, false);
                    }
                }
            }).catch(() => {
                // Ignorar errores de API
            });
            
            return;
        }
        
        // Lectura normal (con cache)
        const plan = await this.getPlanCached(true, finalAccountId);
        if (plan) {
            const state = this.getPlanState(finalAccountId) || 'created';
            if (state === 'active') {
                btn.innerHTML = `
                    <div class="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    Plan Activo
                `;
                btn.className = 'relative px-4 py-2 bg-green-600/20 text-green-400 font-medium rounded-lg border border-green-500/50 hover:bg-green-600/30 transition-all duration-300 flex items-center gap-2 text-sm';
            } else {
                btn.innerHTML = `
                    <div class="w-2 h-2 bg-orange-500 rounded-full"></div>
                    Plan Creado
                `;
                btn.className = 'relative px-4 py-2 bg-orange-600/20 text-orange-300 font-medium rounded-lg border border-orange-500/50 hover:bg-orange-600/30 transition-all duration-300 flex items-center gap-2 text-sm';
            }
        } else if (this.isCreationLocked(finalAccountId)) {
            const when = this.formatNextAllowedAt(this.getNextAllowedAt(finalAccountId));
            btn.innerHTML = `
                <div class="w-2 h-2 bg-amber-400 rounded-full"></div>
                Plan en espera
            `;
            btn.title = when ? `Disponible: ${when}` : 'Disponible al iniciar el próximo día';
            btn.className = 'relative px-4 py-2 bg-amber-600/10 text-amber-300 font-medium rounded-lg border border-amber-500/40 cursor-not-allowed transition-all duration-300 flex items-center gap-2 text-sm';
        } else {
            this.clearPlanState(finalAccountId);
            // Crear Plan style
            btn.innerHTML = `
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                </svg>
                Crear Plan
            `;
            btn.className = 'px-4 py-2 bg-slate-800 text-slate-300 font-medium rounded-lg border border-slate-600 hover:bg-slate-700 hover:border-cyan-500/50 transition-all duration-300 flex items-center gap-2 text-sm';
        }
    }

    async getPlanCached(silent = false, accountId = null) {
        const now = Date.now();
        const finalAccountId = accountId || window.currentAccount;
        // FIX: Cache separado por cuenta
        if (this.planCache[finalAccountId] !== undefined && (now - (this.planCacheTime[finalAccountId] || 0)) < 5000) {
            return this.planCache[finalAccountId];
        }
        const storage = this.getPlanStorage();
        if (!storage) return null;
        this.planCache[finalAccountId] = await storage.getPlan(silent, finalAccountId);
        this.planCacheTime[finalAccountId] = now;
        return this.planCache[finalAccountId];
    }

    async getPlanFresh(accountId = null) {
        const finalAccountId = accountId || window.currentAccount;
        const storage = this.getPlanStorage();
        if (!storage) return null;
        // Leer fresh sin cache, silent mode
        return await storage.getPlan(true, finalAccountId);
    }

    clearPlanCache(accountId = null) {
        const finalAccountId = accountId || window.currentAccount;
        this.planCache[finalAccountId] = undefined; // FIX: Limpiar cache de la cuenta especificada
        this.planCacheTime[finalAccountId] = 0;
    }

    getTodayKey() {
        return new Date().toISOString().split('T')[0];
    }

    getPlanStateKey(accountId) {
        const today = this.getTodayKey();
        return `PLAN_STATE_${accountId}_${today}`;
    }

    getNextAllowedKey(accountId) {
        const today = this.getTodayKey();
        return `PLAN_NEXT_ALLOWED_${accountId}_${today}`;
    }

    getPlanState(accountId) {
        if (!accountId) return null;
        try {
            const key = this.getPlanStateKey(accountId);
            return localStorage.getItem(key) || null;
        } catch (e) {
            return null;
        }
    }

    setPlanState(accountId, state) {
        if (!accountId) return;
        try {
            const key = this.getPlanStateKey(accountId);
            if (!state) {
                localStorage.removeItem(key);
                return;
            }
            localStorage.setItem(key, state);
        } catch (e) {}
    }

    setNextAllowedAt(accountId, isoString) {
        if (!accountId) return;
        try {
            const key = this.getNextAllowedKey(accountId);
            if (!isoString) {
                localStorage.removeItem(key);
                return;
            }
            localStorage.setItem(key, isoString);
        } catch (e) {}
    }

    getNextAllowedAt(accountId) {
        if (!accountId) return '';
        try {
            const key = this.getNextAllowedKey(accountId);
            return localStorage.getItem(key) || '';
        } catch (e) {
            return '';
        }
    }

    isCreationLocked(accountId) {
        const nextAllowedAt = this.getNextAllowedAt(accountId);
        if (!nextAllowedAt) return false;
        const dt = new Date(nextAllowedAt);
        if (Number.isNaN(dt.getTime())) return false;
        return Date.now() < dt.getTime();
    }

    clearPlanState(accountId) {
        this.setPlanState(accountId, null);
    }

    isPlanForToday(plan) {
        if (!plan || !plan.created_at) return false;
        const createdDate = new Date(plan.created_at);
        if (Number.isNaN(createdDate.getTime())) return false;
        
        // FIX: Comparar de forma robusta considerando que el plan puede estar en UTC
        // Usar la hora UTC para la comparación
        const planDateUTC = createdDate.toISOString().split('T')[0];
        const todayUTC = new Date().toISOString().split('T')[0];
        
        // Si coinciden en UTC, es del día actual
        if (planDateUTC === todayUTC) return true;
        
        // También comparar con la fecha local para casos donde el servidor está en UTC pero el cliente en otra zona
        const planDateLocal = createdDate.toISOString().split('T')[0];
        const todayLocal = this.getTodayKey();
        return planDateLocal === todayLocal;
    }

    isCurrentAccountSynced(accountId = null) {
        const finalAccountId = accountId || window.currentAccount;
        const account = window.accounts ? window.accounts[finalAccountId] : null;
        if (!account) return false;
        return !!account.mt_account_number;
    }

    async open() {
        if (window.demoGate && window.demoGate('create_plan', 'Crear Plan')) {
            return;
        }
        if (!this.isCurrentAccountSynced()) {
            if (window.showAlert) {
                window.showAlert('Cuenta no sincronizada', 'Solo puedes crear planes en cuentas sincronizadas.', 'warning');
            }
            return;
        }
        this.existingPlan = await this.getPlanCached();

        if (!this.existingPlan && this.isCreationLocked(window.currentAccount)) {
            const when = this.formatNextAllowedAt(this.getNextAllowedAt(window.currentAccount));
            if (window.showAlert) {
                window.showAlert('Plan en espera', when ? `Podrás crear otro plan a partir de: ${when}` : 'Podrás crear otro plan al iniciar el próximo día.', 'warning');
            }
            return;
        }

        if (!this.existingPlan) {
            this.renderCreatePlan();
        } else {
            this.renderExistingPlan();
        }

        this.show();
    }

    show() {
        this.modal.classList.remove('hidden', 'opacity-0', 'scale-95');
        this.modal.classList.add('opacity-100', 'scale-100');
        this.overlay.classList.remove('hidden');
        this.isOpen = true;
    }

    async close(options = {}) {
        if (!this.isOpen) return;
        const shouldDeactivate = options.deactivateSession !== false;
        try {
            if (shouldDeactivate && window.tradeControl && typeof window.tradeControl.deactivateAutoManagementSession === 'function') {
                await window.tradeControl.deactivateAutoManagementSession(window.currentAccount);
            }
        } catch (e) {}

        try {
            const accountId = window.currentAccount;
            if (accountId) {
                const state = this.getPlanState(accountId);
                if (shouldDeactivate && state === 'active') {
                    this.setPlanState(accountId, 'created');
                    this.updateButtonLabel(accountId);
                }
            }
        } catch (e) {}

        this.modal.classList.add('opacity-0', 'scale-95');
        this.modal.classList.remove('opacity-100', 'scale-100');
        this.overlay.classList.add('hidden');
        setTimeout(() => {
            this.modal.classList.add('hidden');
            this.isOpen = false;
        }, 300);
    }

    /**
     * Crear Nuevo Plan
     */
    renderCreatePlan() {
        this.planValues = {
            trading_session: [],
            pair_category: [],
            risk_level: '',
            profit_goal: '',
            max_daily_loss: '',
            max_trades_day: 10
        };

        const uiBuilder = (typeof PlanUIBuilder !== 'undefined') ? PlanUIBuilder : (window.PlanUIBuilder || null);
        if (!uiBuilder) {
            this.modal.innerHTML = `
                <div class="p-6 text-center text-slate-300">
                    No se pudo cargar el módulo del plan. Recarga la página.
                </div>
            `;
            return;
        }

        const html = `
            <!-- HEADER -->
            <div class="sticky top-0 bg-slate-900 border-b border-slate-700 p-4 lg:p-6 flex items-center justify-between z-10">
                <div>
                    <h2 class="text-lg lg:text-2xl font-bold text-white">MI PLAN DE TRADING</h2>
                    <p class="text-slate-400 text-[11px] lg:text-sm mt-1">Define tu plan antes de operar</p>
                </div>
                <button onclick="window.planManagerV2.close()" class="text-slate-400 hover:text-white transition-colors p-2 hover:bg-slate-800 rounded-lg">
                    <svg class="w-5 h-5 lg:w-6 lg:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                </button>
            </div>

            <!-- FORM -->
            <div class="p-4 lg:p-6 space-y-4 lg:space-y-6 overflow-y-auto scrollbar-thin flex-1 min-h-0 modal-scroll">
                ${uiBuilder.buildTradingSession()}
                ${uiBuilder.buildPairCategory()}
                ${uiBuilder.buildRiskLevel()}
                ${uiBuilder.buildProfitGoal()}
                ${uiBuilder.buildMaxDailyLoss()}
                ${uiBuilder.buildMaxTradesDay()}

                <!-- GUARDAR -->
                <div class="pt-4 border-t border-slate-700">
                    <button onclick="window.planManagerV2.savePlan()" 
                            class="w-full py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold rounded-lg transition transform hover:scale-105">
                        CREAR PLAN
                    </button>
                </div>
            </div>
        `;

        this.modal.innerHTML = html;
    }

    /**
     * Mostrar Plan Existente
     */
    renderExistingPlan() {
        const plan = this.existingPlan;
        const html = `
            <!-- HEADER -->
            <div class="flex items-center justify-between p-3 sm:p-4 border-b border-slate-700/50 shrink-0">
                <div>
                    <h2 class="text-base sm:text-lg font-black text-emerald-400">TU PLAN ACTIVO</h2>
                    <p class="text-[11px] text-gray-400 mt-0.5">Creado el ${new Date(plan.created_at).toLocaleDateString('es-ES')}</p>
                </div>
                <button onclick="window.planManagerV2.close()" class="text-xl text-gray-400 hover:text-white transition p-1">✕</button>
            </div>

            <!-- RESUMEN -->
            <div class="p-3 sm:p-4 overflow-y-auto scrollbar-thin modal-scroll flex-1 min-h-0">
                <div class="bg-slate-800/50 rounded-lg p-4 border border-slate-700 space-y-2 text-sm">
                    <div class="flex justify-between">
                        <span class="text-gray-400">Sesión:</span>
                        <span class="text-white font-bold">${this.formatArray(plan.trading_session_value)}</span>
                    </div>
                    <div class="flex justify-between">
                        <span class="text-gray-400">Pares:</span>
                        <span class="text-white font-bold">${this.formatArray(plan.pair_category_value)}</span>
                    </div>
                    <div class="flex justify-between">
                        <span class="text-gray-400">Riesgo:</span>
                        <span class="text-yellow-400 font-bold">${plan.risk_level_value}</span>
                    </div>
                    <div class="flex justify-between">
                        <span class="text-gray-400">Objetivo:</span>
                        <span class="text-emerald-400 font-bold">+$${plan.profit_goal_value}</span>
                    </div>
                    <div class="flex justify-between">
                        <span class="text-gray-400">Máx Pérdida:</span>
                        <span class="text-red-400 font-bold">-$${plan.max_daily_loss_value}</span>
                    </div>
                    <div class="flex justify-between">
                        <span class="text-gray-400">Máx Trades:</span>
                        <span class="text-white">${plan.max_trades_day_value}/día</span>
                    </div>
                </div>

                <!-- BOTONES -->
                <div class="flex gap-2 mt-4">
                    <button onclick="window.planManagerV2.openFollowPlanModal()" 
                            class="flex-1 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-bold rounded-lg transition">
                        CONFIGURAR
                    </button>
                    <button onclick="window.planManagerV2.deletePlan()" 
                            class="flex-1 py-2 bg-red-600/20 border border-red-500/40 hover:bg-red-600/30 text-white font-bold rounded-lg transition text-center">
                        BORRAR
                    </button>
                </div>
            </div>
        `;

        this.modal.innerHTML = html;
    }

    /**
     * Multi-select toggle (sesiones y pares)
     */
    toggleMultiSelect(btn, field) {
        const value = btn.dataset.value;
        const arr = this.planValues[field];
        const idx = arr.indexOf(value);

        if (idx > -1) {
            arr.splice(idx, 1);
            btn.classList.remove('bg-blue-600', 'bg-emerald-600', 'border-blue-400', 'border-emerald-400', 'text-white');
            btn.classList.add('bg-slate-700', 'border-slate-600', 'text-slate-300');
        } else {
            arr.push(value);
            if (field === 'trading_session') {
                btn.classList.add('bg-blue-600', 'border-blue-400', 'text-white');
            } else {
                btn.classList.add('bg-emerald-600', 'border-emerald-400', 'text-white');
            }
            btn.classList.remove('bg-slate-700', 'border-slate-600', 'text-slate-300');
        }

    }

    /**
     * Set value (inputs simples)
     */
    setValue(field, value) {
        this.planValues[field] = value;
    }

    /**
     * Set toggle (SI/NO)
     */
    setToggle(field, value) {
        this.planValues[field] = value;
        
        // Update UI
        const container = event.target.parentElement;
        const buttons = container.querySelectorAll('button');
        buttons.forEach(btn => {
            btn.classList.remove('bg-emerald-600', 'border-emerald-400', 'bg-slate-600', 'border-slate-500');
            btn.classList.add('bg-slate-700', 'border-slate-600', 'text-slate-300');
        });
        
        if (value) {
            buttons[0].classList.add('bg-emerald-600', 'border-emerald-400', 'text-white');
            buttons[0].classList.remove('bg-slate-700', 'border-slate-600', 'text-slate-300');
        } else {
            buttons[1].classList.add('bg-slate-600', 'border-slate-500', 'text-white');
            buttons[1].classList.remove('bg-slate-700', 'border-slate-600', 'text-slate-300');
        }

    }

    /**
     * Guardar Plan
     */
    async savePlan() {
        if (window.demoGate && window.demoGate('create_plan', 'Crear Plan')) {
            return;
        }
        // Validar campos obligatorios
        if (this.planValues.trading_session.length === 0) {
            if (window.showAlert) window.showAlert('Validación', 'Selecciona al menos una sesión de trading', 'warning');
            return;
        }
        if (this.planValues.pair_category.length === 0) {
            if (window.showAlert) window.showAlert('Validación', 'Selecciona al menos una categoría de pares', 'warning');
            return;
        }
        if (!this.planValues.risk_level) {
            if (window.showAlert) window.showAlert('Validación', 'Selecciona un nivel de riesgo', 'warning');
            return;
        }
        if (!this.planValues.profit_goal || this.planValues.profit_goal <= 0) {
            if (window.showAlert) window.showAlert('Validación', 'Define un objetivo de ganancia válido', 'warning');
            return;
        }
        if (!this.planValues.max_daily_loss || this.planValues.max_daily_loss <= 0) {
            if (window.showAlert) window.showAlert('Validación', 'Define una pérdida máxima válida', 'warning');
            return;
        }

        const accountId = window.currentAccount;

        try {
            const storage = this.getPlanStorage();
            if (!storage) throw new Error('Plan storage no disponible');
            const response = await storage.createPlan(this.planValues, accountId);
            if (response && response.planId) {
                if (window.showAlert) window.showAlert('Éxito', 'Plan creado exitosamente', 'success');
                this.setPlanState(accountId, 'created');
                this.setNextAllowedAt(accountId, null);
                // Clear cache and reload plan
                this.clearPlanCache();
                this.existingPlan = await this.getPlanCached();
                this.renderExistingPlan();
                this.updateButtonLabel();
            } else {
                if (window.showAlert) window.showAlert('Error', 'Error al crear el plan. Intenta de nuevo', 'error');
            }
        } catch (error) {
            const message = (error && error.message) ? error.message : '';
            const nextAllowedAt = error?.data?.nextAllowedAt;
            if (message.includes('409') || message.includes('Plan already created')) {
                const when = this.formatNextAllowedAt(nextAllowedAt);
                const detail = when
                    ? `Ya tienes un plan reciente. Podrás crear otro a partir de: ${when}`
                    : 'Ya tienes un plan reciente para esta cuenta.';
                if (window.showAlert) window.showAlert('Plan en espera', detail, 'warning');
                this.setPlanState(accountId, 'created');
                if (nextAllowedAt) {
                    this.setNextAllowedAt(accountId, nextAllowedAt);
                }
                this.clearPlanCache();
                this.existingPlan = await this.getPlanCached(true);
                if (this.existingPlan) {
                    this.renderExistingPlan();
                }
                this.updateButtonLabel();
                return;
            }
            if (window.showAlert) window.showAlert('Error', 'Error al crear el plan. Intenta de nuevo', 'error');
        }
    }

    /**
     * Actualizar plan existente
     */
    async updatePlan() {
        const storage = this.getPlanStorage();
        if (!storage) throw new Error('Plan storage no disponible');
        const success = await storage.updatePlan(this.planValues);
        
        if (success) {
            if (window.showAlert) window.showAlert('Éxito', 'Plan actualizado exitosamente', 'success');
            this.close();
            location.reload();
        } else {
            if (window.showAlert) window.showAlert('Error', 'Error al actualizar el plan', 'error');
        }
    }

    /**
     * Activar Plan (abrir panel flotante de seguimiento reutilizando sessionPanel)
     */
    async activatePlan() {
        // Cerrar modal actual
        await this.close({ deactivateSession: false });
        
        // Usar el sessionPanel existente del Trade Control
        if (window.tradeControlUI) {
            window.tradeControlUI.switchMode('in-session');
        } else {
            if (window.showAlert) {
                window.showAlert('Error', 'No se pudo cargar el módulo de seguimiento', 'error');
            }
        }
    }

    /**
     * Borrar Plan
     */
    async deletePlan() {
        const nextAllowedPreview = new Date(Date.now() + 24 * 60 * 60 * 1000).toLocaleString('es-ES', {
            hour: '2-digit',
            minute: '2-digit',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
        const confirmed = window.showConfirm
            ? await window.showConfirm({
                title: 'Borrar plan',
                message: `¿Estás seguro de BORRAR tu plan de trading? Esta acción no se puede deshacer.\n\nDespués de borrarlo, no podrás crear un plan nuevo hasta pasadas 24h.\nFecha y hora aproximada: ${nextAllowedPreview}.`,
                confirmText: 'Borrar',
                cancelText: 'Cancelar',
                type: 'warning'
            })
            : false;

        if (!confirmed) return;

        const accountId = window.currentAccount;

        try {
            const storage = this.getPlanStorage();
            if (!storage) throw new Error('Plan storage no disponible');
            const response = await storage.deletePlan(accountId);
            if (response && response.success) {
                const when = this.formatNextAllowedAt(response.nextAllowedAt);
                const detail = when
                    ? `Plan borrado. Podrás crear otro a partir de: ${when}`
                    : 'Plan borrado correctamente';
                if (window.showAlert) window.showAlert('Éxito', detail, 'success');
                if (window.tradeControl && typeof window.tradeControl.deactivateAutoManagementSession === 'function') {
                    try { await window.tradeControl.deactivateAutoManagementSession(); } catch (e) {}
                }
                this.clearPlanCache();
                this.existingPlan = null;
                this.clearPlanState(accountId);
                if (response.nextAllowedAt) {
                    this.setNextAllowedAt(accountId, response.nextAllowedAt);
                }
                this.close();
                this.updateButtonLabel();
            } else {
                if (window.showAlert) window.showAlert('Error', 'Error al borrar el plan', 'error');
            }
        } catch (error) {
            if (window.showAlert) window.showAlert('Error', 'Error al borrar el plan', 'error');
        }
    }

    /**
     * Format array values
     */
    formatArray(jsonString) {
        try {
            const arr = JSON.parse(jsonString);
            return arr.length > 0 ? arr.join(', ') : 'N/A';
        } catch {
            return 'N/A';
        }
    }

    formatNextAllowedAt(isoString) {
        if (!isoString) return '';
        const dt = new Date(isoString);
        if (Number.isNaN(dt.getTime())) return '';
        return dt.toLocaleString('es-ES', {
            hour: '2-digit',
            minute: '2-digit',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    }

    /**
     * Abrir Modal de Seguimiento Diario
     */
    async openFollowPlanModal() {
        if (window.demoGate && window.demoGate('create_plan', 'Crear Plan')) {
            return;
        }
        // Cargar plan si no existe
        if (!this.existingPlan) {
            try {
                const plan = await this.getPlanCached(true);
                if (plan) {
                    this.existingPlan = plan;
                } else {
                    window.showAlert('No tienes plan activo. Crea uno primero.', 'error');
                    return;
                }
            } catch (error) {
                window.showAlert('Error al cargar el plan', 'error');
                return;
            }
        }

        const plan = this.existingPlan;
        const sessions = JSON.parse(plan.trading_session_value || '[]');
        const categories = JSON.parse(plan.pair_category_value || '[]');
        const accountId = window.currentAccount;
        
        // Inicializar checklist con todos los campos - INDEXADO POR CUENTA
        this.dailyChecklists[accountId] = {
            sessions: {},
            categories: {},
            risk_level: false,
            profit_goal: false,
            max_daily_loss: false,
            max_trades_day: false,
            news_review: false,
            setup_matches_model: false,
            emotional_state: false
        };
        
        // Inicializar sesiones y categorías
        sessions.forEach(s => this.dailyChecklists[accountId].sessions[s] = false);
        categories.forEach(c => this.dailyChecklists[accountId].categories[c] = false);

        // Helper: genera un check-card item
        const checkCard = (key, label, sublabel, color) => {
            return `<label class="block cursor-pointer" data-check-card>
                <input type="checkbox" class="sr-only" data-check-key="${key}">
                <div class="flex items-center gap-3 rounded-lg border border-slate-700/60 bg-slate-800/40 px-3 py-2.5 transition-colors" data-check-surface>
                    <div class="flex-1 min-w-0">
                        <p class="text-xs font-medium text-slate-200 truncate">${label}</p>
                        <p class="text-[10px] text-${color}-300 truncate">${sublabel}</p>
                    </div>
                    <span class="w-5 h-5 shrink-0 rounded-full border-2 border-slate-600 flex items-center justify-center transition-colors" data-check-icon></span>
                </div>
            </label>`;
        };

        // Helper: genera un chip para sesiones/categorías
        const chipItem = (type, value, color) => {
            const display = value.charAt(0).toUpperCase() + value.slice(1);
            return `<label class="cursor-pointer" data-chip-label>
                <input type="checkbox" class="sr-only" data-chip-type="${type}" data-chip-value="${value}">
                <span class="inline-flex w-full items-center justify-center gap-1.5 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1.5 text-[11px] font-medium text-slate-400 transition-colors" data-chip-surface>
                    <span class="w-1.5 h-1.5 rounded-full bg-slate-600 transition-colors" data-chip-dot></span>
                    ${display}
                </span>
            </label>`;
        };

        const html = `
            <!-- HEADER -->
            <div class="bg-slate-900 border-b border-slate-700 p-3 sm:p-4 shrink-0">
                <div class="flex items-center justify-between">
                    <div class="min-w-0">
                        <h2 class="text-base sm:text-lg font-bold text-white truncate">Lista Pre-Operativa</h2>
                        <p class="text-[11px] text-slate-400 mt-0.5">Completa todos los items antes de operar</p>
                    </div>
                    <div class="flex items-center gap-2 shrink-0">
                        <span class="px-2 py-0.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-[10px] font-bold rounded-full">${plan.plan_type || 'PRO'}</span>
                        <button onclick="window.planManagerV2.close()" class="text-slate-400 hover:text-white p-1.5 hover:bg-slate-800 rounded-lg transition-colors">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                        </button>
                    </div>
                </div>
                <!-- Progress -->
                <div class="mt-2 flex items-center gap-2">
                    <div class="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                        <div id="progressBar" class="h-full bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-full transition-all duration-300" style="width:0%"></div>
                    </div>
                    <span class="text-[11px] font-bold text-emerald-400 tabular-nums" id="progressText">0/0</span>
                </div>
            </div>

            <!-- SCROLLABLE CONTENT -->
            <div class="overflow-y-auto flex-1 min-h-0 modal-scroll scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-slate-800/50 hover:scrollbar-thumb-slate-500">
                <div class="p-3 sm:p-4 pb-6 space-y-3">

                    <!-- RESUMEN -->
                    <div class="grid grid-cols-4 gap-1.5 rounded-lg border border-slate-700/40 bg-slate-800/30 p-2">
                        <div class="text-center">
                            <p class="text-[9px] text-slate-500 uppercase">Riesgo</p>
                            <p class="text-[11px] font-semibold text-amber-300">${plan.risk_level_value}</p>
                        </div>
                        <div class="text-center">
                            <p class="text-[9px] text-slate-500 uppercase">Objetivo</p>
                            <p class="text-[11px] font-semibold text-emerald-300">+$${plan.profit_goal_value}</p>
                        </div>
                        <div class="text-center">
                            <p class="text-[9px] text-slate-500 uppercase">Pérd. Máx</p>
                            <p class="text-[11px] font-semibold text-rose-300">-$${plan.max_daily_loss_value}</p>
                        </div>
                        <div class="text-center">
                            <p class="text-[9px] text-slate-500 uppercase">Trades</p>
                            <p class="text-[11px] font-semibold text-cyan-300">${plan.max_trades_day_value}/día</p>
                        </div>
                    </div>

                    <!-- SESIÓN + PARES -->
                    <div class="grid grid-cols-2 gap-2">
                        <div>
                            <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">● Sesión</p>
                            <div class="flex flex-wrap gap-1.5">
                                ${sessions.map(s => chipItem('sessions', s, 'cyan')).join('')}
                            </div>
                        </div>
                        <div>
                            <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">● Pares</p>
                            <div class="flex flex-wrap gap-1.5">
                                ${categories.map(c => chipItem('categories', c, 'emerald')).join('')}
                            </div>
                        </div>
                    </div>

                    <!-- RIESGO & OBJETIVOS -->
                    <div>
                        <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">● Riesgo & Objetivos</p>
                        <div class="grid grid-cols-2 gap-1.5">
                            ${checkCard('risk_level', 'Riesgo', plan.risk_level_value, 'amber')}
                            ${checkCard('profit_goal', 'Objetivo', '+$' + plan.profit_goal_value, 'emerald')}
                            ${checkCard('max_daily_loss', 'Pérdida Máx', '-$' + plan.max_daily_loss_value, 'rose')}
                            ${checkCard('max_trades_day', 'Máx Trades', plan.max_trades_day_value + '/día', 'cyan')}
                        </div>
                    </div>

                    <!-- VERIFICACIONES -->
                    <div>
                        <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">● Verificaciones</p>
                        <div class="grid grid-cols-1 gap-1.5">
                            ${checkCard('news_review', 'Noticias Revisadas', 'Impacto macro y calendario', 'purple')}
                            ${checkCard('setup_matches_model', 'Setup Válido', 'Coincide con tu modelo', 'indigo')}
                            ${checkCard('emotional_state', 'Mentalidad Correcta', 'Enfoque y disciplina', 'rose')}
                        </div>
                    </div>
                </div>
            </div>

            <!-- FOOTER -->
            <div class="shrink-0 border-t border-slate-700 bg-slate-900 p-3 sm:p-4">
                <button id="startFollowingBtn" onclick="window.planManagerV2.activatePlanFromChecklist()"
                        class="w-full py-2.5 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white text-sm font-bold rounded-lg transition-all duration-200 opacity-50 cursor-not-allowed" disabled>
                    <span class="flex items-center justify-center gap-2">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
                        Activar Sesión
                    </span>
                </button>
                <button onclick="window.planManagerV2.close()"
                        class="w-full mt-1.5 py-2 text-xs text-slate-400 hover:text-white font-medium rounded-lg transition-colors hover:bg-slate-800/50">
                    Cancelar
                </button>
            </div>
        `;

        this.modal.innerHTML = html;

        // Wire up event delegation for all checkboxes (no inline handlers)
        this.modal.querySelectorAll('[data-check-key]').forEach(input => {
            input.addEventListener('change', () => {
                const key = input.dataset.checkKey;
                this.toggleChecklistItem(key, null, input.checked);
                const surface = input.closest('[data-check-card]').querySelector('[data-check-surface]');
                const icon = input.closest('[data-check-card]').querySelector('[data-check-icon]');
                if (surface) {
                    surface.classList.toggle('border-emerald-500/60', input.checked);
                    surface.classList.toggle('bg-emerald-500/10', input.checked);
                }
                if (icon) {
                    icon.innerHTML = input.checked ? '<svg class="w-3 h-3 text-emerald-400" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>' : '';
                    icon.classList.toggle('border-emerald-400', input.checked);
                    icon.classList.toggle('bg-emerald-500/20', input.checked);
                    icon.classList.toggle('border-slate-600', !input.checked);
                }
            });
        });

        this.modal.querySelectorAll('[data-chip-type]').forEach(input => {
            input.addEventListener('change', () => {
                const type = input.dataset.chipType;
                const value = input.dataset.chipValue;
                this.toggleChecklistItem(type, value, input.checked);
                const surface = input.closest('[data-chip-label]').querySelector('[data-chip-surface]');
                const dot = input.closest('[data-chip-label]').querySelector('[data-chip-dot]');
                if (surface) {
                    surface.classList.toggle('border-cyan-400/60', input.checked);
                    surface.classList.toggle('bg-cyan-500/15', input.checked);
                    surface.classList.toggle('text-white', input.checked);
                    surface.classList.toggle('text-slate-400', !input.checked);
                }
                if (dot) {
                    dot.classList.toggle('bg-cyan-400', input.checked);
                    dot.classList.toggle('bg-slate-600', !input.checked);
                }
            });
        });

        // Set initial progress count
        this.updateChecklistProgress();
        this.show(); // Mostrar el modal
    }

    /**
     * Get total checkboxes count
     */
    getTotalCheckboxes() {
        const plan = this.existingPlan;
        const sessions = JSON.parse(plan.trading_session_value || '[]');
        const categories = JSON.parse(plan.pair_category_value || '[]');
        // 7 = risk, profit, loss, max_trades, news, setup, emotional
        return sessions.length + categories.length + 7;
    }

    /**
     * Toggle checklist item
     */
    toggleChecklistItem(category, value, checked) {
        const accountId = window.currentAccount;
        if (!this.dailyChecklists[accountId]) {
            this.dailyChecklists[accountId] = {};
        }
        
        if (category === 'sessions' || category === 'categories') {
            if (!this.dailyChecklists[accountId][category]) {
                this.dailyChecklists[accountId][category] = {};
            }
            this.dailyChecklists[accountId][category][value] = checked;
        } else {
            this.dailyChecklists[accountId][category] = checked;
        }
        
        this.updateChecklistProgress();
    }

    /**
     * Update checklist progress
     */
    updateChecklistProgress() {
        const accountId = window.currentAccount;
        const checklist = this.dailyChecklists[accountId] || {};
        
        const total = this.getTotalCheckboxes();
        let checked = 0;
        
        // Count sessions
        Object.values(checklist.sessions || {}).forEach(v => { if (v) checked++; });
        // Count categories
        Object.values(checklist.categories || {}).forEach(v => { if (v) checked++; });
        // Count simple fields
        ['risk_level', 'profit_goal', 'max_daily_loss', 'max_trades_day', 'news_review', 'setup_matches_model', 'emotional_state'].forEach(field => {
            if (checklist[field]) checked++;
        });
        
        // Update progress bar and text
        const progressBarEl = document.getElementById('progressBar');
        const progressTextEl = document.getElementById('progressText');
        const btnEl = document.getElementById('startFollowingBtn');
        
        if (progressBarEl) {
            const percentage = (checked / total) * 100;
            progressBarEl.style.width = percentage + '%';
        }
        
        if (progressTextEl) {
            progressTextEl.textContent = `${checked}/${total}`;
        }
        
        if (btnEl) {
            if (checked === total) {
                btnEl.disabled = false;
                btnEl.classList.remove('opacity-50', 'cursor-not-allowed');
            } else {
                btnEl.disabled = true;
                btnEl.classList.add('opacity-50', 'cursor-not-allowed');
            }
        }
    }

    /**
     * Start following plan - Create draggable widget
     */
    async startFollowing() {
        const plan = this.existingPlan;
        
        // Close modal
        this.close();
        
        // Create draggable widget
        this.createFollowingWidget(plan);
    }

    /**
     * Activate plan from checklist - Opens sessionPanel
     */
    async activatePlanFromChecklist() {
        // Cerrar modal del checklist (no desactivar sesión aquí)
        await this.close({ deactivateSession: false });

        try {
            if (!window.tradeControl) {
                throw new Error('TradeControl no disponible');
            }

            await window.tradeControl.activateAutoManagementSession();

            const accountId = window.currentAccount;
            this.setPlanState(accountId, 'active');
            this.updateButtonLabel();

            // Usar el sessionPanel existente del Trade Control
            if (window.tradeControlUI) {
                window.tradeControlUI.switchMode('in-session');
                if (typeof window.tradeControlUI.showToast === 'function') {
                    window.tradeControlUI.showToast('Sesión iniciada - Buena suerte!', 'success');
                }
            }
        } catch (error) {
            if (window.showAlert) {
                window.showAlert('Error', 'No se pudo activar la sesión en MT. Revisa la cuenta sincronizada.', 'error');
            }
        }
    }

    /**
     * Create draggable floating widget
     */
    createFollowingWidget(plan) {
        // Remove existing widget if any
        const existing = document.getElementById('planFollowingWidget');
        if (existing) existing.remove();
        
        const widget = document.createElement('div');
        widget.id = 'planFollowingWidget';
        widget.className = 'fixed top-20 right-4 w-72 bg-gradient-to-br from-slate-900 to-slate-800 rounded-lg shadow-2xl border border-emerald-500/50 z-[9999] cursor-move';
        widget.style.maxHeight = '80vh';
        
        const sessions = JSON.parse(plan.trading_session_value || '[]');
        const categories = JSON.parse(plan.pair_category_value || '[]');
        
        widget.innerHTML = `
            <div class="bg-gradient-to-r from-emerald-600 to-cyan-600 p-3 rounded-t-lg flex items-center justify-between" id="widgetHeader">
                <div class="flex items-center gap-2">
                    <div class="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                    <span class="text-white font-bold text-sm">PLAN ACTIVO</span>
                </div>
                <button onclick="document.getElementById('planFollowingWidget').remove()" class="text-white hover:text-red-300 text-lg">✕</button>
            </div>
            <div class="p-4 space-y-2 text-xs overflow-y-auto" style="max-height: 60vh">
                <div class="flex justify-between items-center">
                    <span class="text-gray-400">Sesión:</span>
                    <span class="text-white font-bold">${sessions.join(', ')}</span>
                </div>
                <div class="flex justify-between items-center">
                    <span class="text-gray-400">Pares:</span>
                    <span class="text-white font-bold">${categories.join(', ')}</span>
                </div>
                <div class="flex justify-between items-center">
                    <span class="text-gray-400">Riesgo:</span>
                    <span class="text-yellow-400 font-bold">${plan.risk_level_value}</span>
                </div>
                <div class="flex justify-between items-center">
                    <span class="text-gray-400">Objetivo:</span>
                    <span class="text-emerald-400 font-bold">+$${plan.profit_goal_value}</span>
                </div>
                <div class="flex justify-between items-center">
                    <span class="text-gray-400">Máx Pérdida:</span>
                    <span class="text-red-400 font-bold">-$${plan.max_daily_loss_value}</span>
                </div>
                <div class="flex justify-between items-center">
                    <span class="text-gray-400">Máx Trades:</span>
                    <span class="text-cyan-400 font-bold">${plan.max_trades_day_value}/día</span>
                </div>
                <div class="mt-3 pt-3 border-t border-slate-700">
                    <div class="flex items-center gap-2 text-emerald-400">
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path></svg>
                        <span class="font-semibold">Checklist completo</span>
                    </div>
                </div>
            </div>
        `;
        
        document.body.appendChild(widget);
        
        // Make draggable
        this.makeDraggable(widget);
    }

    /**
     * Make element draggable
     */
    makeDraggable(element) {
        const header = element.querySelector('#widgetHeader');
        let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;
        
        header.onmousedown = dragMouseDown;
        
        function dragMouseDown(e) {
            e.preventDefault();
            pos3 = e.clientX;
            pos4 = e.clientY;
            document.onmouseup = closeDragElement;
            document.onmousemove = elementDrag;
        }
        
        function elementDrag(e) {
            e.preventDefault();
            pos1 = pos3 - e.clientX;
            pos2 = pos4 - e.clientY;
            pos3 = e.clientX;
            pos4 = e.clientY;
            element.style.top = (element.offsetTop - pos2) + 'px';
            element.style.left = (element.offsetLeft - pos1) + 'px';
            element.style.right = 'auto';
        }
        
        function closeDragElement() {
            document.onmouseup = null;
            document.onmousemove = null;
        }
    }
}

// Instanciar global
window.planManagerV2 = new PlanManagerV2();
